import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from '@schibsted-svp/react-ui';
import { useFormikContext } from 'formik';
import get from 'lodash/get';

import { Form } from '../Form/Form';
import { SubmitButton } from '../Form/SubmitButton/SubmitButton';
import { Input } from '../Form/Input/Input';
import { Select } from '../Form/Select/Select';
import { CollapsibleSection } from '../Form/CollapsibleSection/CollapsibleSection';
import { Checkbox } from '../Form/Checkbox/Checkbox';
import { RadioGroup } from '../Form/RadioGroup/RadioGroup';
import { getComponentTypeLabel } from '../../helpers/getComponentTypeLabel';
import { CategoriesSelect } from '../Form/CategoriesSelect/CategoriesSelect';
import { isAsset, isCategory } from '../PresetPicker/helpers';
import { usePreviousValue } from '../../hooks/usePreviousValue';

import {
  componentsConfiguration,
  componentTypeDefaultOptions,
} from './configuration';
import { DataSource } from './DataSource/DataSource';
import { resolveDataSourceMode } from './DataSource/resolveDataSourceMode';
import { SaveToSharedButton } from './SaveToSharedButton/SaveToSharedButton';
import { FilteredListFields } from './FilteredListFields/FilteredListFields';
import { SplashFields } from './SplashFields/SplashFields';
import { EditorialFields } from './EditorialFields/EditorialFields';
import { ScheduledFields } from './ScheduledFields/ScheduledFields';

import type {
  ComponentInput,
  ComponentType,
  DataType,
  PresetElement,
} from '../../types';
import type { FC } from 'react';

import styles from './ComponentForm.module.scss';

const toPresetUri = (blob: PresetElement) => {
  if (isAsset(blob)) {
    return `asset:${blob.id}`;
  }
  if (isCategory(blob)) {
    return `category:${blob.id}`;
  }
  return `tag:${blob.id}`;
};

const LayoutOptions = () => {
  const { values } =
    useFormikContext<Partial<ComponentInput & { id: string }>>();

  const useVerticalPosters = values?.options?.useVerticalPosters;
  const componentType = values?.type;
  const dataType = values?.data?.type;

  const hasCheckbox =
    dataType === 'assets' ||
    dataType === 'seasons' ||
    dataType === 'categories' ||
    dataType === 'mixed' ||
    componentType === 'filtered-list' ||
    componentType === 'scheduled';
  const hasRadio = componentType === 'list';
  const hasSortByFlightTimes =
    dataType === 'assets' &&
    (componentType === 'slider' || componentType === 'carousel');
  const hasSortByEpisodeNumber = dataType === 'seasons';
  const hasHideSearchField = componentType === 'filtered-list';

  if (!hasCheckbox && !hasRadio) {
    return null;
  }

  return (
    <CollapsibleSection label="Options" initiallyCollapsed={false}>
      {hasCheckbox &&
        componentType !== 'scheduled' &&
        componentType !== 'carousel' &&
        dataType !== 'categories' &&
        dataType !== 'mixed' && (
          <Checkbox
            label="Use vertical posters"
            name="options.useVerticalPosters"
          />
        )}
      {hasRadio && (
        <RadioGroup
          label="Assets layout"
          name="options.layout"
          disabled={useVerticalPosters}
          options={[
            {
              label: 'Vertical (asset details below the image)',
              value: 'vertical',
            },
            {
              label: 'Horizontal (asset details next to the image)',
              value: 'horizontal',
            },
          ]}
        />
      )}
      {hasSortByFlightTimes && (
        <Checkbox
          label="Sort by flight times"
          name="options.sortByFlightTimes"
        />
      )}
      {hasHideSearchField && (
        <Checkbox label="Hide search field" name="options.hideSearchField" />
      )}
      {componentType === 'scheduled' && (
        <Checkbox label="Full-fledged" name="options.isFullFledged" />
      )}
      {hasSortByEpisodeNumber && (
        <RadioGroup
          label="Sort by episode number"
          name="options.sortByEpisodeNumber"
          options={[
            {
              label: 'Ascending (oldest episodes first)',
              value: 'asc',
            },
            {
              label: 'Descending (latest episodes first)',
              value: 'desc',
            },
          ]}
        />
      )}
      {componentType === 'slider' &&
        (dataType === 'categories' ||
          dataType === 'assets' ||
          dataType === 'mixed') && (
          <>
            <Checkbox
              label="Use large thumbnails"
              name="options.useLargeThumbnails"
            />
            <Checkbox
              label="Skip metadata (do not display title and description below thumbnails)"
              name="options.skipMetadata"
            />
          </>
        )}
    </CollapsibleSection>
  );
};

/**
 * C4: resets Formik state when the component type changes on a new (unsaved)
 * draft. Runs as a sibling to the form body so it has access to Formik context.
 * Only fires on truly new components (isNew=true) — editing an existing
 * component has type disabled so this path is unreachable.
 */
export const TypeChangeReset: FC<{ isNew: boolean }> = ({ isNew }) => {
  const { values, setValues } =
    useFormikContext<Partial<ComponentInput & { id: string }>>();
  const componentType = values?.type;
  const previousType = usePreviousValue(componentType);

  useEffect(() => {
    if (!isNew) return;
    if (!previousType || componentType === previousType) return;

    // Rebuild values as { type, isShared, ...typeDefaults }, dropping all
    // type-specific fields (categoryId, header, content, editor, filters,
    // overrides, data, options.*) that belong to the previous type.
    const typeDefaults = componentType
      ? (componentTypeDefaultOptions[componentType as ComponentType] ?? {})
      : {};

    setValues({
      type: componentType,
      isShared: values.isShared ?? false,
      options: Object.keys(typeDefaults).length > 0 ? typeDefaults : undefined,
    });
  }, [componentType, previousType, isNew, values.isShared, setValues]);

  return null;
};

type Props = {
  component?: Partial<ComponentInput & { id: string }>;
  onCancelClick?: () => void;
  onSubmit: (
    componentDraft: Partial<
      ComponentInput & {
        id: string;
        pageId: string;
        index: number;
      }
    >
  ) => Promise<void>;
  isStandalone?: boolean;
};

export const ComponentForm: FC<Props> = ({
  component = {},
  onCancelClick,
  onSubmit,
  isStandalone = false,
}) => {
  const isNew = !component.id;
  const inputsSize = isStandalone ? 'big' : 'small';

  const componentsTypesSelectOptions =
    componentsConfiguration.componentsTypes.map((type) => ({
      value: type,
      label: getComponentTypeLabel(type),
    }));

  /**
   * C7: build `data` explicitly so that read-only virtuals (`preset`, `url`)
   * never reach the write payload. `preset2` is derived from the resolved
   * preset blobs and omitted entirely (not set to []) when the data type has
   * no preset in the configuration at all (Plan A). For preset-capable
   * types in 'endpoint' mode, `preset2` is set to `[]` so the API actually
   * clears a previously-stored preset (an omitted key is read by the API as
   * "keep the existing preset").
   *
   * buildComponentWritePayload (C1/C2) further sanitizes the full draft before
   * it reaches the API call, but we construct the right `data` shape here so
   * that the Formik values passed to onSubmit are already correct.
   */
  const handleSubmit = useCallback(
    (componentDraft: Partial<ComponentInput & { id: string }>) => {
      const type = componentDraft.type;
      const data = componentDraft.data as Record<string, unknown> | undefined;

      // For filter-only types, omit data entirely (schema expects it absent).
      const filterOnlyTypes = ['filtered-list', 'scheduled'];
      if (type && filterOnlyTypes.includes(type)) {
        return onSubmit({ ...componentDraft, data: undefined });
      }

      if (!data) {
        return onSubmit(componentDraft);
      }

      // Find the configuration entry for the current data type
      const componentConfig =
        componentsConfiguration.dataSources[
          type as keyof typeof componentsConfiguration.dataSources
        ];
      const dataType = data.type as string | undefined;
      const entry =
        componentConfig && dataType
          ? (
              componentConfig as ReadonlyArray<{
                dataType: string;
                endpoints?: readonly string[];
                preset?: boolean;
              }>
            ).find((e) => e.dataType === dataType)
          : null;

      const hasPreset = Boolean(entry?.preset);
      const hasEndpoints = Boolean(
        entry?.endpoints && entry.endpoints.length > 0
      );

      // Derive preset2 from resolved preset blobs only when the data type
      // supports a preset. Omit the key entirely (not []) when it does not —
      // an empty [] is truthy and breaks the seasons-slider population plan
      // (Plan A regression).
      const preset = get(data, 'preset', []) as PresetElement[];
      // C-DSM: resolve the effective preset/endpoint mode so that, on save,
      // only the active side's data is kept. `data.dataSourceMode` itself is
      // a transient client-only field and is never copied into sanitizedData
      // below.
      const mode = resolveDataSourceMode({
        hasEndpoints,
        hasPreset,
        storedMode: data.dataSourceMode,
        hasPresetItems: preset.length > 0,
      });
      // In 'endpoint' mode, preset2 must be an explicit empty array — not
      // omitted. The API merges `incoming.preset2 ?? existing.preset2`, so
      // omitting the key is read as "keep the existing preset", while `[]`
      // is the only way to actually clear a previously-stored preset.
      const preset2 = !hasPreset
        ? undefined
        : mode === 'preset'
          ? preset.map(toPresetUri)
          : [];

      const sanitizedData: ComponentInput['data'] = {
        type: dataType as DataType,
      };
      // Use a plain object cast to avoid TS errors from the discriminated union:
      // seasons never has source/preset2 (hasPreset=false, no endpoints), so
      // these assignments are unreachable for seasons in practice.
      const mutableData = sanitizedData as Record<string, unknown>;
      if (data.source !== undefined) mutableData.source = data.source;
      if (preset2 !== undefined) mutableData.preset2 = preset2;

      return onSubmit({ ...componentDraft, data: sanitizedData });
    },
    [onSubmit]
  );

  return (
    <Form
      initialValues={component}
      onSubmit={handleSubmit}
      className={classNames(styles.container, {
        [styles.isStandalone]: isStandalone,
      })}
    >
      {({ values: { data = {}, type: componentType } }) => (
        <>
          <TypeChangeReset isNew={isNew} />
          {isStandalone && <Input label="Shared name" name="sharedName" />}
          <Select
            label="Type"
            name="type"
            disabled={!isNew}
            options={componentsTypesSelectOptions}
            size={inputsSize}
          />
          {componentType !== 'splash' &&
            componentType !== 'carousel' &&
            data.type !== 'seasons' && (
              <Input label="Header" name="header" size={inputsSize} />
            )}
          {componentType === 'scheduled' && <ScheduledFields />}
          {componentType === 'editorial' && (
            <EditorialFields inputsSize={inputsSize} />
          )}
          <DataSource inputsSize={inputsSize} />
          {data.type === 'seasons' && (
            <CategoriesSelect
              label="Series"
              name="categoryId"
              params={{ filter: 'isSeries::true' }}
            />
          )}
          {componentType === 'splash' && (
            <SplashFields inputsSize={inputsSize} />
          )}
          {componentType === 'filtered-list' && (
            <FilteredListFields isStandalone={isStandalone} />
          )}
          {componentType !== 'splash' &&
            componentType !== 'continue-watching' && <LayoutOptions />}
          <div className={styles.buttons}>
            {!isNew && !isStandalone && (
              <SaveToSharedButton
                disabled={component.isShared}
                onSubmit={(values) => onSubmit({ ...values, id: component.id })}
              />
            )}
            {typeof onCancelClick === 'function' && (
              <Button
                size="small"
                type="button"
                variant="standard"
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            )}
            <SubmitButton
              size={isStandalone ? 'big' : 'small'}
              className={styles.submit}
            >
              {isNew ? 'Create component' : 'Save'}
            </SubmitButton>
          </div>
        </>
      )}
    </Form>
  );
};
